// Gen-Z celebration sound effects using Web Audio API
class SoundManager {
  private audioContext: AudioContext | null = null;

  // Check if sounds are enabled in settings
  private isSoundEnabled(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false;
    return localStorage.getItem("kai_sound_enabled") === "true";
  }

  private getContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }
  
  // Force play sound regardless of settings (for explicit user actions)
  forcePlayKaiInteract() {
    this._playKaiInteract();
  }

  // Satisfying "Start Your Journey" sound - magical whoosh with chime
  playStartJourney() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Magical whoosh sweep
    const sweep = ctx.createOscillator();
    const sweepGain = ctx.createGain();
    sweep.connect(sweepGain);
    sweepGain.connect(ctx.destination);
    sweep.type = 'sine';
    sweep.frequency.setValueAtTime(200, now);
    sweep.frequency.exponentialRampToValueAtTime(2000, now + 0.4);
    sweepGain.gain.setValueAtTime(0.3, now);
    sweepGain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    sweep.start(now);
    sweep.stop(now + 0.5);

    // Sparkle chimes
    [0.1, 0.2, 0.3, 0.4].forEach((time, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200 + i * 200, now + time);
      gain.gain.setValueAtTime(0.15, now + time);
      gain.gain.exponentialRampToValueAtTime(0.01, now + time + 0.15);
      osc.start(now + time);
      osc.stop(now + time + 0.2);
    });

    // Final satisfying "ding"
    const ding = ctx.createOscillator();
    const dingGain = ctx.createGain();
    ding.connect(dingGain);
    dingGain.connect(ctx.destination);
    ding.type = 'sine';
    ding.frequency.setValueAtTime(880, now + 0.5);
    dingGain.gain.setValueAtTime(0.4, now + 0.5);
    dingGain.gain.exponentialRampToValueAtTime(0.01, now + 1.2);
    ding.start(now + 0.5);
    ding.stop(now + 1.2);
  }

  // Gen-Z style "Level Up" sound - upbeat electronic
  playLevelUp() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Main melody with rising pitch
    [0, 0.15, 0.3].forEach((time, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440 * Math.pow(2, i * 0.5), now + time);
      
      gain.gain.setValueAtTime(0.3, now + time);
      gain.gain.exponentialRampToValueAtTime(0.01, now + time + 0.3);
      
      osc.start(now + time);
      osc.stop(now + time + 0.3);
    });

    // Bass drop
    const bass = ctx.createOscillator();
    const bassGain = ctx.createGain();
    bass.connect(bassGain);
    bassGain.connect(ctx.destination);
    bass.type = 'sawtooth';
    bass.frequency.setValueAtTime(110, now + 0.45);
    bassGain.gain.setValueAtTime(0.4, now + 0.45);
    bassGain.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
    bass.start(now + 0.45);
    bass.stop(now + 0.8);
  }

  // Mission complete - quick victory sound
  playMissionComplete() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Celebratory fanfare
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.1);
      
      gain.gain.setValueAtTime(0.25, now + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.3);
      
      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.3);
    });
  }

  // Streak achieved - sparkle sound
  playStreak() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // High pitch sparkle
    for (let i = 0; i < 5; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1000 + Math.random() * 1000, now + i * 0.05);
      
      gain.gain.setValueAtTime(0.15, now + i * 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.05 + 0.1);
      
      osc.start(now + i * 0.05);
      osc.stop(now + i * 0.05 + 0.1);
    }
  }

  // XP gain - coin collect sound
  playXpGain() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
    
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    
    osc.start(now);
    osc.stop(now + 0.2);
  }

  // Button click - subtle satisfying pop
  playClick() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(400, now + 0.05);
    
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
    
    osc.start(now);
    osc.stop(now + 0.1);
  }

  // Kai appear sound - gentle magical pop
  playKaiAppear() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Soft whoosh up
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.2);
    
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
    
    osc.start(now);
    osc.stop(now + 0.3);

    // Sparkle accent
    const sparkle = ctx.createOscillator();
    const sparkleGain = ctx.createGain();
    sparkle.connect(sparkleGain);
    sparkleGain.connect(ctx.destination);
    
    sparkle.type = 'sine';
    sparkle.frequency.setValueAtTime(1200, now + 0.1);
    
    sparkleGain.gain.setValueAtTime(0.08, now + 0.1);
    sparkleGain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    
    sparkle.start(now + 0.1);
    sparkle.stop(now + 0.25);
  }

  // Kai interaction sound - friendly boop
  playKaiInteract() {
    if (!this.isSoundEnabled()) return;
    this._playKaiInteract();
  }

  private _playKaiInteract() {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(500, now);
    osc.frequency.exponentialRampToValueAtTime(700, now + 0.08);
    osc.frequency.exponentialRampToValueAtTime(600, now + 0.12);
    
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
    
    osc.start(now);
    osc.stop(now + 0.18);
  }
  // Cinematic intro ambient sound - low drone with subtle pulse
  playIntroAmbient() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Low frequency drone
    const drone = ctx.createOscillator();
    const droneGain = ctx.createGain();
    drone.connect(droneGain);
    droneGain.connect(ctx.destination);
    drone.type = 'sine';
    drone.frequency.setValueAtTime(60, now);
    droneGain.gain.setValueAtTime(0.08, now);
    droneGain.gain.linearRampToValueAtTime(0.12, now + 1);
    droneGain.gain.linearRampToValueAtTime(0.08, now + 2);
    drone.start(now);
    drone.stop(now + 3);
  }

  // Card swipe sound - satisfying whoosh
  playCardSwipe(direction: 'left' | 'right' | 'up') {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    
    if (direction === 'right') {
      // Positive swipe - ascending
      osc.frequency.setValueAtTime(300, now);
      osc.frequency.exponentialRampToValueAtTime(600, now + 0.15);
      gain.gain.setValueAtTime(0.15, now);
    } else if (direction === 'left') {
      // Negative swipe - descending
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.exponentialRampToValueAtTime(200, now + 0.12);
      gain.gain.setValueAtTime(0.1, now);
    } else {
      // Curious swipe - neutral wave
      osc.frequency.setValueAtTime(350, now);
      osc.frequency.exponentialRampToValueAtTime(500, now + 0.08);
      osc.frequency.exponentialRampToValueAtTime(400, now + 0.15);
      gain.gain.setValueAtTime(0.12, now);
    }
    
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    osc.start(now);
    osc.stop(now + 0.25);
  }

  // Step transition sound - gentle progress chime
  playStepTransition() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    const notes = [523, 659]; // C5, E5
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.08);
      gain.gain.setValueAtTime(0.12, now + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.2);
      osc.start(now + i * 0.08);
      osc.stop(now + i * 0.08 + 0.25);
    });
  }

  // Intro complete fanfare
  playIntroComplete() {
    if (!this.isSoundEnabled()) return;
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Rising sweep
    const sweep = ctx.createOscillator();
    const sweepGain = ctx.createGain();
    sweep.connect(sweepGain);
    sweepGain.connect(ctx.destination);
    sweep.type = 'sine';
    sweep.frequency.setValueAtTime(200, now);
    sweep.frequency.exponentialRampToValueAtTime(800, now + 0.3);
    sweepGain.gain.setValueAtTime(0.2, now);
    sweepGain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
    sweep.start(now);
    sweep.stop(now + 0.5);

    // Celebratory chord
    const chordNotes = [523, 659, 784];
    chordNotes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + 0.35);
      gain.gain.setValueAtTime(0.15, now + 0.35);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.9);
      osc.start(now + 0.35);
      osc.stop(now + 1);
    });
  }
}

export const soundManager = new SoundManager();
